import { useEffect, useRef } from 'react';
import { callServer } from './server.ts';

// Calls to the investment routes of the sync function. The API secret travels once, over the
// authenticated request, straight to the server — it is never stored on this device (spec §41A).

const call = <T = unknown>(householdId: string, path: string, body?: unknown) =>
  callServer<T>(`/investments${path}`, { method: 'POST', query: { householdId }, body, unreachable: 'INVESTMENT_UNAVAILABLE' });

export type ConnectBody = {
  provider: 'TRADING212' | 'FAKE_INVEST';
  environment: 'LIVE' | 'DEMO';
  apiKey: string;
  apiSecret: string;
  accountKind: 'INVEST' | 'STOCKS_ISA';
  accountId: string | null;
};

export const investmentsApi = {
  connect: (householdId: string, body: ConnectBody) => call<{ connectionId: string; accountId: string }>(householdId, '/connect', body),
  refresh: (householdId: string, connectionId: string) => call(householdId, `/${connectionId}/refresh`),
  disconnect: (householdId: string, connectionId: string) => call(householdId, `/${connectionId}/disconnect`),
};

/** How old an investment account's figures may get while the app is open. */
const FRESH_MS = 15 * 60 * 1000;

/**
 * Keeps investment accounts current while the app is in use: on opening, on coming back to the
 * window and every 15 minutes while it is visible, a connection read more than 15 minutes ago is
 * refreshed (the server's own 15-minute round stops while it is not running). One request per
 * connection at a time; a failure waits for the next round.
 */
export function useInvestmentAutoRefresh(
  householdId: string | null,
  connections: readonly { id: string; status: string; lastSyncedAt: string | null; deletedAt: string | null }[],
  onRefreshed: () => void,
): void {
  const latest = useRef({ householdId, connections, onRefreshed });
  latest.current = { householdId, connections, onRefreshed };
  const asked = useRef(new Map<string, number>());

  useEffect(() => {
    const check = () => {
      const { householdId: household, connections: list, onRefreshed: done } = latest.current;
      if (!household || document.visibilityState !== 'visible') return;
      const now = Date.now();
      for (const c of list) {
        if (c.deletedAt !== null || c.status === 'DISCONNECTED') continue;
        const read = c.lastSyncedAt ? Date.parse(c.lastSyncedAt) : 0;
        if (now - read < FRESH_MS || now - (asked.current.get(c.id) ?? 0) < FRESH_MS) continue;
        asked.current.set(c.id, now);
        void investmentsApi.refresh(household, c.id).then(done, () => undefined);
      }
    };
    const first = setTimeout(check, 3_000);
    const timer = setInterval(check, 60_000);
    addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearTimeout(first);
      clearInterval(timer);
      removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);
}
